import { useMemo } from "react";
import type {} from "@mui/lab/themeAugmentation"; // https://mui.com/material-ui/about-the-lab/#typescript
import { createTheme, useColorScheme } from "@mui/material";
import themeDarkColors from "./base.colors.dark";
import themeLightColors from "./base.colors.light";
import { createThemeSettings } from "./createTheme";

// Export types from theme.d.ts for convenience
export type {
  InputSizes,
  RunStatusPalette,
  ThemePalette,
} from "./theme.d.ts";

/** Returns design tokens for the given resolved color scheme. Use `useTokens()` in components. */
export const getTokens = (
  colorScheme: "light" | "dark" | undefined
): typeof themeDarkColors | typeof themeLightColors => {
  return colorScheme === "dark" ? themeDarkColors : themeLightColors;
};

// color design tokens export
export const useTokens = (): typeof themeDarkColors | typeof themeLightColors => {
  const { colorScheme } = useColorScheme();
  return getTokens(colorScheme ?? "light");
};

export const useMode = () => {
  return useMemo(() => {
    const theme = createTheme(createThemeSettings());
    return {
      ...theme,
      inputSizes: {
        small: "200px",
        medium: "300px",
        large: "400px",
      },
    };
  }, []);
};

export const colorMap = {
  running: "info",
  succeeded: "success",
  success: "success",
  finished: "success",
  completed: "success",
  resolved: "success",
  passed: "success",
  stopped: "warning",
  halted: "warning",
  // unknown: "warning",
  // unknown_status: "warning",
  p2: "warning",
  warning: "warning",
  alert: "warning",
  failed: "error",
  p1: "error",
  error: "error",
  break: "error",

  volume: "info",
  time: "success",
  schema: "warning",
  //distribution: "",

  covered: "success",
  "not covered": "neutral",
} as const;

export type ColorKeys = keyof typeof colorMap;

export const getColor = (status?: ColorKeys) => {
  if (!status) {
    return "neutral";
  } else if (colorMap[status]) {
    return colorMap[status];
  } else if (colorMap[status.toLowerCase() as ColorKeys]) {
    return colorMap[status.toLowerCase() as ColorKeys];
  }
  return "neutral";
};
